import React, { useState } from 'react'
import PropTypes from 'prop-types'
import parse from 'html-react-parser';
import GoogleMapReact from 'google-map-react';
import { capitalCities } from '../../../utils/constants';
import generalController from '../../../controllers/generalController';
import Input from '../../1_Atoms/Forms/Input/Input';
import Button from '../../1_Atoms/Button/Button';
import { Svg } from '../../1_Atoms/Svg/Svg';
import { languageController } from '../../../controllers/language/languageController';
import { convertPostalToCoordinates, getPreviousUserlocation, getUserGeoLocation, storeUserLocation } from '../../../utils/googleMapHelper';
import formValidationController from '../../../controllers/formValidationController';

const EVCircleCenterDot = () => (
  <div className="mz-ev-range-map__center-dot"></div>
)

function EVRangeMap({
  formExtraParams
}) {
  const { introductionText, radiusValue, disclaimerText } = formExtraParams;
  const [postalCode, setPostalCode] = useState('')
  const [statusMessage, setStatusMessage] = useState({ text: '', isError: false })
  const [errorMessage, setErrorMessage] = useState('')
  const [currentCircle, setCurrentCircle] = useState(null)
  const [maps, setMaps] = useState(null)
  const [map, setMap] = useState(null)
  const [userLocation, setUserLocation] = useState({ lat: 0, lng: 0 })

  const drawCircle = (map, maps, center) => {
    // Remove the current Circle
    if (currentCircle) {
      currentCircle.setMap(null)
    }

    // Create a new Circle
    const newCircle = new maps.Circle({
      strokeColor: "#910A2D",
      strokeWeight: 2,
      fillColor: "#910A2D",
      fillOpacity: 0.2,
      map,
      center: center,
      radius: Number(radiusValue) * 1000,
    });

    // Set the new circle
    setCurrentCircle(newCircle)
  }

  // Set map circle, etc, when google map API gets loaded.
  const handleAPILoaded = (map, maps) => {
    let mapCenter;
    const prevUserLocationData = getPreviousUserlocation();

    // Check if user location already exists in localstorage (geolocation or postalcode)
    if (prevUserLocationData) {
      // Geolocation
      if (prevUserLocationData.type === 'geolocation') {
        mapCenter = prevUserLocationData.value;
      }
      // Postalcode
      else if (prevUserLocationData.type === 'postalcode') {
        const { coordinates, postalcode } = prevUserLocationData.value;
        mapCenter = coordinates;
        // Set postalcode in the input.
        setPostalCode(postalcode)
      }
    }
    // If it doesn't exist use capital city of user province.
    else {
      const capitalData = capitalCities[generalController.getMetatagValue('province')];
      if (capitalData) {
        mapCenter = capitalData.location;
      }
    }

    if (mapCenter) {
      // Set new center as user location
      setUserLocation(mapCenter)
      // Draw a map circle
      drawCircle(map, maps, mapCenter)
      // Set map instances on states
      setMaps(maps)
      setMap(map)
    }
  }

  // Handle updating map upon search.
  const onSearchLocation = async (postalCode) => {
    try {
      if (!postalCode) return;

      // Validate the postalcode value.
      if (formValidationController.validateField(postalCode, 'postalcode')) {
        if (errorMessage) setErrorMessage('');

        setStatusMessage({ text: languageController.getCopy('evRangeMap', 'showRangeLoading'), isError: false })

        const res = await convertPostalToCoordinates(postalCode);

        if (res) {
          // Set new center as user location
          setUserLocation(res)
          // Draw a new circle
          drawCircle(map, maps, res)
          // Store user postalcode data in localstorage
          storeUserLocation('postalcode', { postalcode: postalCode, coordinates: res })

          setStatusMessage({ isError: false, text: '' })
          return;
        }

        setErrorMessage(languageController.getError('general'))
      } else {
        setErrorMessage(languageController.getError('postalcode'))
      }
    } catch (err) {
      console.log('Error: ', err)
      if (err.response) {
        setErrorMessage(err.response.data?.message)
      }
    }
  }

  // Search dealers based on user's geolocation.
  const onGeolocationSubmit = async () => {
    setStatusMessage({ text: languageController.getCopy('findDealer', 'geoLoading'), isError: false })
    const res = await getUserGeoLocation();

    if (res.success) {
      setStatusMessage({ isError: false, text: '' })
      drawCircle(map, maps, res.data)
      // Set new center as user location
      setUserLocation(res.data)
      // Set user geolocation in localstorage.
      storeUserLocation('geolocation', res.data)
    } else {
      setStatusMessage({ text: res.data, isError: true })
      // Remove error message after 3s
      setTimeout(() => {
        setStatusMessage({ isError: false, text: '' })
      }, 3000)
    }
  }

  return (
    <div className="mz-ev-range-map">
      <div className="mz-container">
        <div className="mz-full-width">
          {/* Intro text */}
          {introductionText && (
            <div className="mz-ev-range-map__intro-text">
              {parse(introductionText)}
            </div>
          )}
          {/* Search bar */}
          <div className="mz-ev-range-map__search">
            <div className="mz-ev-range-map__search-inner">
              <div className="mz-ev-range-map__input">
                <Input
                  type="text"
                  name="postalcode"
                  label={languageController.getFormCopy('label', 'postalcode')}
                  value={postalCode}
                  onChange={(e) => setPostalCode(e.target.value)}
                  maxLength={7}
                />
                <Button
                  className="mz-btn--primary"
                  label={languageController.getCopy('evRangeMap', 'searchCTA')}
                  onClick={() => onSearchLocation(postalCode)}
                />
              </div>
              {
                errorMessage && (
                  <div className="mz-ev-range-map__error-message">{errorMessage}</div>
                )
              }
              <p className="text--grey paragraph">{languageController.getCopy('findDealer', 'or')}</p>
              <Button
                className="mz-btn--primary mz-ev-range-map__geolocation-btn"
                label={languageController.getCopy('findDealer', 'useGeolocation')}
                onClick={onGeolocationSubmit}
                buttonIcon={<Svg type="locationairplane" size={1.2} colour="white" style={{ top: '1px' }} />}
                buttonIconPosition="right"
              />
              <div className={`mz-ev-range-map__status-message ${statusMessage.text ? 'active' : ''}`}>
                <Svg type={statusMessage.isError ? 'warning' : 'sync'} size={1} colour="white" style={{ top: '1px' }} />
                <span className="interactive">{statusMessage.text}</span>
              </div>
            </div>
          </div>
          {/* Map widget */}
          <div className="mz-ev-range-map__map-widget">
            <GoogleMapReact
              bootstrapURLKeys={{
                key: process.env.REACT_APP_GOOGLE_MAP_KEY,
              }}
              defaultCenter={{ lat: 0, lng: 0 }}
              center={userLocation}
              zoom={10}
              options={{
                minZoom: 6,
                maxZoom: 14,
                fullscreenControl: false
              }}
              yesIWantToUseGoogleMapApiInternals
              onGoogleApiLoaded={({ map, maps }) => handleAPILoaded(map, maps)}
            >
              <EVCircleCenterDot
                lat={userLocation.lat}
                lng={userLocation.lng}
              />
            </GoogleMapReact>
          </div>
          {/* Disclaimer */}
          {
            disclaimerText && (
              <div className="mz-ev-range-map__disclaimer label text--grey">
                {parse(disclaimerText)}
              </div>
            )
          }
        </div>
      </div>
    </div>
  )
}

EVRangeMap.propTypes = {
  formExtraParams: PropTypes.object.isRequired
}

EVRangeMap.defaultProps = {
  formExtraParams: {}
}

export default EVRangeMap